import type { FormEvent } from "react";
import { useState } from "react";
import type { AxiosError } from "axios";
import { useCreateSuperuser } from "../hooks";

const MIN_PASSWORD_LENGTH = 8;

type ErrorPayload = {
  detail?: string;
  message?: string;
};

type CreateSuperuserFormProps = {
  embedded?: boolean;
};

const getErrorMessage = (error: unknown): string => {
  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object" && "isAxiosError" in error) {
    const axiosError = error as AxiosError<ErrorPayload>;
    return (
      axiosError.response?.data?.detail ??
      axiosError.response?.data?.message ??
      axiosError.message ??
      "Unable to create superuser right now."
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Unable to create superuser right now.";
};

export const CreateSuperuserForm = ({ embedded }: CreateSuperuserFormProps) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  const createSuperuserMutation = useCreateSuperuser();

  const canSubmit =
    email.trim().length > 0 &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    password.trim().length >= MIN_PASSWORD_LENGTH &&
    confirmPassword.trim().length >= MIN_PASSWORD_LENGTH &&
    !createSuperuserMutation.isPending;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    setFormSuccess(null);

    if (password !== confirmPassword) {
      setFormError("Passwords do not match.");
      return;
    }

    if (password.trim().length < MIN_PASSWORD_LENGTH) {
      setFormError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    try {
      await createSuperuserMutation.mutateAsync({
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password
      });
      setFormSuccess(`Superuser ${email.trim()} created successfully.`);
      setEmail("");
      setFirstName("");
      setLastName("");
      setPassword("");
      setConfirmPassword("");
    } catch (error) {
      setFormError(getErrorMessage(error));
    }
  };

  const inner = (
    <>
      <h2>Add super user</h2>
      <p>Create another administrator for the provisioning portal.</p>
      <form onSubmit={handleSubmit}>
        <div className="settings-form-two-col">
          <label style={{ display: "grid", gap: "0.35rem", minWidth: 0 }}>
            <span className="settings-label">First name</span>
            <input className="settings-input" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
          </label>
          <label style={{ display: "grid", gap: "0.35rem", minWidth: 0 }}>
            <span className="settings-label">Last name</span>
            <input className="settings-input" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
          </label>
        </div>
        <label style={{ display: "grid", gap: "0.35rem" }}>
          <span className="settings-label">Work email</span>
          <input
            className="settings-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            autoComplete="off"
            required
          />
        </label>
        <label style={{ display: "grid", gap: "0.35rem" }}>
          <span className="settings-label">Temporary password</span>
          <div className="settings-password-row">
            <input
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              required
            />
            <button type="button" onClick={() => setShowPw((v) => !v)}>
              {showPw ? "Hide" : "Show"}
            </button>
          </div>
        </label>
        <label style={{ display: "grid", gap: "0.35rem" }}>
          <span className="settings-label">Confirm temporary password</span>
          <div className="settings-password-row">
            <input
              type={showPw2 ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              required
            />
            <button type="button" onClick={() => setShowPw2((v) => !v)}>
              {showPw2 ? "Hide" : "Show"}
            </button>
          </div>
        </label>
        <p style={{ margin: 0, fontSize: "0.78rem", color: "#94a3b8" }}>Minimum {MIN_PASSWORD_LENGTH} characters.</p>
        {formError ? <div style={{ color: "#b91c1c", fontSize: "0.875rem" }}>{formError}</div> : null}
        {formSuccess ? <div style={{ color: "#166534", fontSize: "0.875rem" }}>{formSuccess}</div> : null}
        <div className="settings-form-actions">
          <button type="submit" disabled={!canSubmit} className="settings-btn-primary">
            {createSuperuserMutation.isPending ? "Creating…" : "Create super user"}
          </button>
        </div>
      </form>
    </>
  );

  if (embedded) {
    return <div className="settings-form-embedded">{inner}</div>;
  }

  return <div className="settings-form-card">{inner}</div>;
};

export default CreateSuperuserForm;
